import { useEffect, useRef } from "react";
import { COPY, WIDGET_EMBED_CODE } from "@/config";
import { LiveDot } from "@/App";

// Pulls the widget id out of a `_wf.push({ id: '...' })` snippet so we can
// pre-create the exact mount node (`#wf_main_<id>`) the embed looks for.
// Without it, the embed appends its root to <body> — the MutationObserver
// below moves any such stray node back into the card.
const parseWidgetId = (code) => {
  const m = code.match(/_wf\.push\(\s*\{[^}]*?id:\s*['"]([^'"]+)['"]/);
  return m ? m[1] : null;
};

// Opens the provider's own popup when the widget is configured as a floater.
// We never touch form fields or submission — this only activates the widget's
// own trigger element (its public API's show() is a no-op for this type).
export const openWidgetFloater = () => {
  const box = document.querySelector(".wf_floater_box");
  if (box && getComputedStyle(box).opacity === "1") return; // already open
  const trigger =
    document.querySelector(".wf_handler_logo") ||
    document.querySelector(".wf_floater_handler [class*='wf_handler_icon']") ||
    document.querySelector(".wf_floater_handler");
  trigger?.click();
};

export const WidgetEmbed = ({ flash = false, showOpenButton = true, onModeChange }) => {
  const hostRef = useRef(null);
  const code = (WIDGET_EMBED_CODE || "").trim();
  const widgetId = code ? parseWidgetId(code) : null;
  const onModeChangeRef = useRef(onModeChange);
  onModeChangeRef.current = onModeChange;

  useEffect(() => {
    const host = hostRef.current;
    if (!host || !code) return undefined;

    // Keep the embed idempotent under React StrictMode double-mounting:
    // the vendor script guards on window._wf_initialized and polls this queue.
    if (code.includes("_wf")) window._wf = [];

    // Inject the raw HTML, then re-create every <script> so it actually
    // executes (dangerouslySetInnerHTML/innerHTML alone never runs scripts).
    host.innerHTML = code;
    host.querySelectorAll("script").forEach((oldScript) => {
      const s = document.createElement("script");
      Array.from(oldScript.attributes).forEach((attr) =>
        s.setAttribute(attr.name, attr.value),
      );
      s.text = oldScript.textContent;
      oldScript.replaceWith(s);
    });

    // Fallback: if the embed ignores our mount node and appends its root to
    // <body>, move it into the card.
    const reclaimStrays = () => {
      document
        .querySelectorAll('body > div[id^="wf_main_"]')
        .forEach((node) => {
          if (!host.contains(node)) host.appendChild(node);
        });
    };
    const observer = new MutationObserver(reclaimStrays);
    observer.observe(document.body, { childList: true });
    reclaimStrays();

    // Detect how the provider configured this widget: an inline form fills
    // the mount node (height > 0); a floater renders only fixed-position
    // popup nodes (its .wf_floater_handler trigger appears), leaving the
    // mount node collapsed.
    let settled = false;
    const settle = (mode) => {
      if (settled) return;
      settled = true;
      window.clearInterval(poll);
      window.clearTimeout(timeout);
      onModeChangeRef.current?.(mode);
    };
    const poll = window.setInterval(() => {
      if (host.getBoundingClientRect().height > 40) settle("inline");
      else if (document.querySelector(".wf_floater_handler")) settle("floater");
    }, 300);
    const timeout = window.setTimeout(() => settle("floater"), 8000);

    return () => {
      observer.disconnect();
      window.clearInterval(poll);
      window.clearTimeout(timeout);
    };
  }, [code]);

  return (
    <div
      data-testid="widget-card"
      className={`w-full max-w-[440px] rounded-xl border-[0.5px] bg-[var(--surface)] p-6 transition-shadow duration-300 sm:p-8 ${
        flash
          ? "border-[var(--accent)] shadow-[0_0_0_4px_rgba(232,179,60,0.25)]"
          : "border-[var(--border)]"
      }`}
    >
      {code ? (
        <>
          {showOpenButton ? (
            <>
              <p
                data-testid="session-now-line"
                className="mb-4 flex items-center justify-center gap-2 text-sm font-semibold text-[var(--text)]"
              >
                <LiveDot />
                {COPY.sessionNowLine}
              </p>
              <button
                type="button"
                data-testid="widget-open-button"
                onClick={openWidgetFloater}
                className="flex w-full items-center justify-center gap-2 rounded-md bg-[var(--accent)] px-6 py-4 text-base font-bold text-[var(--accent-ink)] transition-colors duration-200 hover:bg-[#f2c254] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:ring-offset-2 focus:ring-offset-[var(--bg)]"
              >
                {COPY.finalCtaButton}
              </button>
            </>
          ) : null}
          <div
            ref={hostRef}
            data-testid="widget-mount"
            id={widgetId ? `wf_main_${widgetId}` : undefined}
            className={widgetId ? `wf_target_${widgetId}` : undefined}
          />
        </>
      ) : (
        <div
          data-testid="widget-placeholder"
          className="flex min-h-[280px] items-center justify-center rounded-lg border-[0.5px] border-dashed border-[var(--border)] bg-[var(--bg)] p-8 text-center text-sm font-medium text-[var(--muted)]"
        >
          Registration widget goes here.
        </div>
      )}
    </div>
  );
};
